import { E_AlarmLevel, E_AlarmType } from '../enums'
import { E_YesNoNumber, IPageData, IPageParams } from '../types'
import { E_DealStatus } from './alarm.types'
import { E_Axis } from './sensor.types'

/** 报警语音合成状态 */
export enum E_AlarmVoiceStatus {
    Pending = 0,
    Processing = 1,
    Success = 2,
    Failed = 3,
}

/** 报警语音播报方式 */
export enum E_AlarmVoiceBroadcastMode {
    Once = 1,
    RepeatUntilHandled = 2,
}

/** 分页查询报警语音推送记录参数 */
export interface IAlarmVoicePageListParams extends Partial<IPageParams> {
    /** 报警等级 */
    alarmLevel?: E_AlarmLevel
    /** 报警类型列表，支持多选，多个值以逗号分隔 */
    alarmTypeList?: string
    /** 区域ID */
    areaId?: number
    /** 公司ID */
    companyId?: number
    /** 结束时间 */
    endTime?: string
    /** 设备ID */
    equipmentId?: number
    /** 主键ID */
    id?: number
    /** 测点ID */
    mappingId?: number
    /** 总公司ID，平台管理员可指定 */
    parentCompanyId?: number
    /** 传感器ID */
    sensorId?: string
    /** 开始时间 */
    startTime?: string
    /** 合成状态 */
    status?: E_AlarmVoiceStatus
}

/** 报警语音推送记录 */
export interface IAlarmVoiceRecord {
    /** 报警轴向 */
    alarmAxis?: E_Axis
    /** 报警记录ID */
    alarmId?: number
    /** 报警等级 */
    alarmLevel?: E_AlarmLevel
    /** 报警阈值，格式为“预警阈值,危险阈值” */
    alarmThreshold?: string
    /** 报警时间 */
    alarmTime?: string
    /** 报警类型 */
    alarmType?: E_AlarmType
    /** 报警值 */
    alarmValue?: number
    /** 区域ID */
    areaId?: number
    /** 区域名称 */
    areaName?: string
    /** 音频播放地址 */
    audioUrl?: string
    /** 播报方式 */
    broadcastMode?: E_AlarmVoiceBroadcastMode
    /** 公司ID */
    companyId?: number
    /** 公司名称 */
    companyName?: string
    /** 播报文案快照 */
    content?: string
    /** 报警处理状态 */
    dealStatus?: E_DealStatus
    /** 设备ID */
    equipmentId?: number
    /** 设备名称 */
    equipmentName?: string
    /** 最近一次合成错误 */
    errorMessage?: string
    /** 音频文件ID */
    fileId?: number
    /** 主键ID */
    id?: number
    /** 测点ID */
    mappingId?: number
    /** 测点名称 */
    mappingName?: string
    /** 总公司ID */
    parentCompanyId?: number
    /** 总公司名称 */
    parentCompanyName?: string
    /** 重复次数 */
    repeatCount?: number
    /** 已重试次数 */
    retryCount?: number
    /** 传感器ID */
    sensorId?: string
    /** 合成状态 */
    status?: E_AlarmVoiceStatus
}

/** Swagger 分页结果包含 pages，不包含公共分页类型中的 orders */
export type IAlarmVoicePageListData = Partial<Omit<IPageData<IAlarmVoiceRecord>, 'orders'>> & {
    pages?: number
}

/** 查询当前总公司语音播报配置参数 */
export interface IAlarmVoiceSettingParams {
    /** 总公司ID，平台管理员可指定 */
    parentCompanyId?: number
}

/** 报警语音播报配置 */
export interface IAlarmVoiceSetting {
    /** 播报报警等级开关 */
    alarmLevelFlag?: E_YesNoNumber
    /** 播报报警类型开关 */
    alarmTypeFlag?: E_YesNoNumber
    /** 播报区域开关 */
    areaFlag?: E_YesNoNumber
    /** 播报轴向开关 */
    axisFlag?: E_YesNoNumber
    /** 播报方式 */
    broadcastMode?: E_AlarmVoiceBroadcastMode
    /** 播报公司开关 */
    companyFlag?: E_YesNoNumber
    /** 自定义播报文案 */
    customText?: string
    /** 危险播报开关 */
    dangerFlag?: E_YesNoNumber
    /** 语音播报开关 */
    enabledFlag?: E_YesNoNumber
    /** 播报设备开关 */
    equipmentFlag?: E_YesNoNumber
    /** 主键ID */
    id?: number
    /** 播报测点开关 */
    mappingFlag?: E_YesNoNumber
    /** 总公司ID */
    parentCompanyId?: number
    /** 重复播报次数 */
    repeatCount?: number
    /** 播报传感器ID开关 */
    sensorFlag?: E_YesNoNumber
    /** 播报报警阈值开关 */
    thresholdFlag?: E_YesNoNumber
    /** 播报报警时间开关 */
    timeFlag?: E_YesNoNumber
    /** 播报报警值开关 */
    valueFlag?: E_YesNoNumber
    /** Kokoro 中文音色 speaker ID（zf_* 为女声，zm_* 为男声） */
    voiceId?: number
    /** 预警播报开关 */
    warningFlag?: E_YesNoNumber
}

/** 保存当前总公司语音播报配置参数 */
export type IAlarmVoiceSaveSettingParams = Omit<IAlarmVoiceSetting, 'id'>
